// SPDX-License-Identifier: BUSL-1.1

/**
 * Flag lifecycle health card — shows lifecycle stage distribution and stale flag detection.
 *
 * Surfaces "stale" flags (not updated in 30+ days) and "zombie candidates"
 * (disabled with no recent activity) to help operators maintain flag hygiene.
 *
 */

import type * as React from 'react';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { Flag } from '@/types/api';
import { LIFECYCLE_COLORS } from '@/types/api';

interface FlagLifecycleCardProps {
  flags: Flag[] | undefined;
  isLoading: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  draft: 'Draft',
  rollout: 'Rollout',
  graduated: 'Graduated',
  sunset: 'Sunset',
};

const STALE_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function FlagLifecycleCard({ flags, isLoading }: FlagLifecycleCardProps): React.ReactElement {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Lifecycle Health</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-5 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  const data = flags ?? [];
  const now = Date.now();

  // Count per lifecycle stage
  const stageCounts: Record<string, number> = {};
  for (const flag of data) {
    const stage = flag.lifecycleStage ?? 'unknown';
    stageCounts[stage] = (stageCounts[stage] ?? 0) + 1;
  }

  // Stale flags: not updated in > 30 days
  const staleCount = data.filter(
    (f) => now - new Date(f.updatedAt).getTime() > STALE_THRESHOLD_MS,
  ).length;

  // Zombie candidates: disabled AND stale
  const zombieCount = data.filter(
    (f) => !f.enabled && now - new Date(f.updatedAt).getTime() > STALE_THRESHOLD_MS,
  ).length;

  // Ordered stages for display
  const orderedStages = ['draft', 'rollout', 'graduated', 'sunset'];
  const stageEntries = orderedStages
    .filter((s) => stageCounts[s] !== undefined)
    .map((s) => [s, stageCounts[s] ?? 0] as const);

  // Add any unknown stages not in our ordered list
  for (const [stage, count] of Object.entries(stageCounts)) {
    if (!orderedStages.includes(stage)) {
      stageEntries.push([stage, count]);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Lifecycle Health</CardTitle>
        <Link
          to="/flags"
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          Review →
        </Link>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Stage distribution */}
        <div className="flex flex-wrap gap-2">
          {stageEntries.map(([stage, count]) => (
            <span
              key={stage}
              className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs ${LIFECYCLE_COLORS[stage] ?? 'bg-neutral-800 text-neutral-400'}`}
            >
              {STAGE_LABELS[stage] ?? stage}
              <span className="font-mono">{count}</span>
            </span>
          ))}
        </div>

        {/* Stale flags warning */}
        <div className="space-y-2 border-t border-neutral-800 pt-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-500">Stale flags (30d+)</span>
            <span
              className={`font-mono text-sm ${staleCount > 0 ? 'text-amber-400' : 'text-neutral-500'}`}
            >
              {staleCount}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-500">Zombie candidates</span>
            <span
              className={`font-mono text-sm ${zombieCount > 0 ? 'text-red-400' : 'text-neutral-500'}`}
            >
              {zombieCount}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
